import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCcw } from "lucide-react";
import { PageHeader, Pill, SectionCard } from "../../components/Ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  PurchaseBriefDialog,
  VoucherBriefDialog,
} from "../../components/purchase/FinancialAdjustmentBriefDialogs";
import { api, extractError } from "../../lib/api";
import { formatListDate } from "../../lib/purchaseListMeta";
import { toast } from "sonner";
import {
  DonutMeter,
  ExpenseBreakdownCard,
  MajorKpiGrid,
  formatCurrency,
  formatMoney2,
  moneyToneClass,
  formatPartyKind,
  formatPct,
  formatQty,
} from "./purchaseReportShared";
import BackendExportButton from "../../components/BackendExportButton";
import PartyTdsAnalysisPanel from "../../components/PartyTdsAnalysisPanel";

const LAB_ANALYSIS_PARAMS = [
  { key: "moisture_pct", label: "Moisture %" },
  { key: "yield_pct", label: "Yield %" },
  { key: "broken_pct", label: "Broken %" },
  { key: "damage_pct", label: "Damage / Discolour %" },
  { key: "green_pct", label: "Green %" },
  { key: "immature_pct", label: "Immature %" },
  { key: "admixture_pct", label: "Admixture %" },
  { key: "pr_pct", label: "PR %" },
  { key: "length_mm", label: "Length mm" },
  { key: "choba_pct", label: "Choba %" },
  { key: "kachri_pct", label: "Kachri %" },
];

const DEFAULT_FILTERS = {
  from_date: "",
  to_date: "",
  variety: "",
  broker: "",
  warehouse: "",
};

export default function PurchasePartyProfilePage() {
  const { id } = useParams();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [tab, setTab] = useState("contracts");
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);

  const params = useMemo(
    () => ({
      from_date: filters.from_date || undefined,
      to_date: filters.to_date || undefined,
      variety: filters.variety || undefined,
      broker: filters.broker || undefined,
      warehouse: filters.warehouse || undefined,
    }),
    [filters],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/purchase-reports/party/${id}`, { params });
      setPayload(data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [id, params]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="purchase-party-profile-page">
      <PageHeader
        eyebrow="Purchase Reports"
        title={payload?.party?.name || "Party Deep-Dive Profile"}
        description="Operational and financial drilldown for a single procurement party with contract fulfillment, lab analysis, and adjustment visibility."
        actions={
          <>
            <Button type="button" variant="outline" asChild>
              <Link to="/purchase/reports">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to dashboard
              </Link>
            </Button>
            <Button type="button" variant="outline" onClick={load} disabled={loading}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
          </>
        }
      />

      <SectionCard title="Profile Scope">
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={payload?.party?.kind === "SUPPLIER" ? "sage" : "gold"}>
            {formatPartyKind(payload?.party?.kind)}
          </Pill>
          <span className="text-sm text-muted-foreground">Use the filters below to narrow this party’s delivery and commercial history.</span>
        </div>
      </SectionCard>

      <SectionCard title="Filters">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <Field label="From date">
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))} />
          </Field>
          <Field label="To date">
            <Input type="date" value={filters.to_date} onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))} />
          </Field>
          <Field label="Variety">
            <Input placeholder="PR-14, 1121..." value={filters.variety} onChange={(e) => setFilters((f) => ({ ...f, variety: e.target.value }))} />
          </Field>
          <Field label="Broker">
            <Input placeholder="Broker name..." value={filters.broker} onChange={(e) => setFilters((f) => ({ ...f, broker: e.target.value }))} />
          </Field>
          <Field label="Warehouse">
            <Input placeholder="Stack / location..." value={filters.warehouse} onChange={(e) => setFilters((f) => ({ ...f, warehouse: e.target.value }))} />
          </Field>
        </div>
        <div className="mt-4">
          <Button type="button" variant="outline" onClick={() => setFilters(DEFAULT_FILTERS)}>Clear filters</Button>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_2fr] gap-4">
        <SectionCard title="Fulfillment Snapshot">
          <DonutMeter
            value={payload?.kpis?.fulfillment_pct}
            center={`${formatQty(payload?.kpis?.received_qty)} qtl`}
            label="Received"
            sublabel={`${formatQty(payload?.kpis?.pending_qty)} qtl pending against ${formatQty(payload?.kpis?.contracted_qty)} qtl contracted`}
          />
        </SectionCard>
        <MajorKpiGrid
          items={[
            { label: "Contracted qty", value: `${formatQty(payload?.kpis?.contracted_qty)} qtl`, accent: true },
            { label: "Received qty", value: `${formatQty(payload?.kpis?.received_qty)} qtl` },
            { label: "Pending receivable qty", value: `${formatQty(payload?.kpis?.pending_qty)} qtl`, accent: true },
            { label: "Weighted avg rate", value: formatCurrency(payload?.kpis?.weighted_avg_rate), sub: "Received · per qtl" },
            { label: "Contracted weighted avg", value: formatCurrency(payload?.kpis?.contracted_weighted_avg_rate), sub: "Contract · per qtl" },
            { label: "Outstanding balance", value: formatCurrency(payload?.kpis?.outstanding_balance) },
            { label: "True landed cost / qtl", value: formatCurrency(payload?.kpis?.true_landed_cost_per_qtl) },
          ]}
        />
      </div>

      <ExpenseBreakdownCard
        rows={payload?.expenses_summary?.rows || []}
        drilldown={payload?.expenses_summary?.drilldown || {}}
        total={payload?.expenses_summary?.true_landed_cost || 0}
        perQtl={payload?.expenses_summary?.true_landed_cost_per_qtl || 0}
        landedCostSection={payload?.expenses_summary?.landed_cost_section}
        vendorDeductionsSection={payload?.expenses_summary?.vendor_deductions_section}
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="contracts">Contracts Ledger</TabsTrigger>
          <TabsTrigger value="deliveries">Lab Analysis</TabsTrigger>
          <TabsTrigger value="financials">Financial Adjustments</TabsTrigger>
          <TabsTrigger value="tds">TDS analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="contracts">
          <SectionCard
            title="Contracts Ledger"
            action={(
              <div className="flex flex-wrap items-center gap-2">
                <BackendExportButton
                  path={`/purchase-reports/party/${id}/contracts-ledger/export/excel`}
                  params={params}
                  filename={`contracts-ledger-${id}.csv`}
                  label="Export Excel"
                  testId="contracts-ledger-export-excel"
                />
                <BackendExportButton
                  path={`/purchase-reports/party/${id}/contracts-ledger/export/pdf`}
                  params={params}
                  filename={`contracts-ledger-${id}.pdf`}
                  label="Export PDF"
                  testId="contracts-ledger-export-pdf"
                />
              </div>
            )}
          >
            <SimpleTable
              columns={["Contract", "Date", "Quality", "Rate", "Contracted", "Received", "Pending", "Progress", "Status"]}
              rows={(payload?.contracts_ledger || []).map((row) => [
                row.contract_number,
                formatListDate(row.contract_date),
                row.quality || "—",
                formatCurrency(row.agreed_rate),
                `${formatQty(row.quantity_qtl)} qtl`,
                `${formatQty(row.received_qtl)} qtl`,
                `${formatQty(row.pending_qtl)} qtl`,
                formatPct(row.progress_pct),
                <div key={`status-${row.id}`} className="flex flex-wrap gap-2">
                  <Pill tone={row.stale_alert ? "warning" : "sage"}>{row.status}</Pill>
                  {row.stale_alert ? <Pill tone="danger">Needs closure</Pill> : null}
                </div>,
              ])}
              emptyMessage={loading ? "Loading contracts..." : "No contracts found for this party."}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="deliveries">
          <SectionCard
            title="Lab Analysis"
            subtitle="Truck / composite lab results with contract rate, net amount, and unload destination."
            action={(
              <div className="flex flex-wrap items-center gap-2">
                <BackendExportButton
                  path={`/purchase-reports/party/${id}/lab-analysis/export/excel`}
                  params={params}
                  filename={`lab-analysis-${id}.csv`}
                  label="Export Excel"
                  testId="lab-analysis-export-excel"
                />
                <BackendExportButton
                  path={`/purchase-reports/party/${id}/lab-analysis/export/pdf`}
                  params={params}
                  filename={`lab-analysis-${id}.pdf`}
                  label="Export PDF"
                  testId="lab-analysis-export-pdf"
                />
              </div>
            )}
          >
            <LabAnalysisTable
              rows={payload?.lab_analysis || payload?.inward_deliveries || []}
              weightedAvg={payload?.lab_analysis_weighted_avg}
              loading={loading}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="financials">
          <SectionCard
            title="Financial Adjustments"
            subtitle="Material bill with expense columns, linked freight voucher, and party vs BCPL freight split."
            action={(
              <div className="flex flex-wrap items-center gap-2">
                <BackendExportButton
                  path={`/purchase-reports/party/${id}/financial-adjustments/export/excel`}
                  params={params}
                  filename={`financial-adjustments-${id}.csv`}
                  label="Export Excel"
                  testId="financial-adjustments-export-excel"
                />
                <BackendExportButton
                  path={`/purchase-reports/party/${id}/financial-adjustments/export/pdf`}
                  params={params}
                  filename={`financial-adjustments-${id}.pdf`}
                  label="Export PDF"
                  testId="financial-adjustments-export-pdf"
                />
              </div>
            )}
          >
            <FinancialAdjustmentsTable
              rows={payload?.financial_adjustments || []}
              loading={loading}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="tds">
          <PartyTdsAnalysisPanel partyId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="space-y-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function EmptyText({ text }) {
  return <div className="py-12 text-center text-sm text-muted-foreground">{text}</div>;
}

function FinancialAdjustmentsTable({ rows, loading }) {
  const [voucherPreview, setVoucherPreview] = useState(null);
  const [purchasePreview, setPurchasePreview] = useState(null);

  const expenseColumns = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      for (const exp of row.expenses || []) {
        const key = exp.key || exp.code || exp.label;
        if (!key || map.has(key)) continue;
        map.set(key, exp.label || key);
      }
    }
    return [...map.entries()].map(([key, label]) => ({ key, label }));
  }, [rows]);

  const freightAdjColumns = useMemo(() => {
    const map = new Map();
    for (const row of rows) {
      for (const adj of row.freight_adjustments || []) {
        const key = adj.key || adj.code || adj.label;
        const label = String(adj.label || key || "");
        if (!key || map.has(key)) continue;
        // Party advance is shown as material "Freight advance", not after Gross freight.
        if (/party\s*advance/i.test(label) || /party-advance/i.test(String(key))) continue;
        map.set(key, label || key);
      }
    }
    return [...map.entries()].map(([key, label]) => ({ key, label }));
  }, [rows]);

  if (!rows.length) {
    return <EmptyText text={loading ? "Loading financial adjustments..." : "No financial adjustments found."} />;
  }

  const columns = [
    { key: "date", label: "Date", wrap: false },
    { key: "voucher", label: "Voucher", wrap: false },
    { key: "invoice", label: "Invoice number", wrap: true },
    { key: "purchase", label: "Purchase", wrap: false },
    { key: "gross", label: "Gross amt", wrap: true, money: true },
    ...expenseColumns.map((col) => ({ key: `exp-${col.key}`, label: col.label, wrap: true, money: true })),
    { key: "freight_advance", label: "Freight advance", wrap: true, money: true },
    { key: "net", label: "Net amount", wrap: true, money: true },
    { key: "freight_voucher", label: "Freight voucher no", wrap: true },
    { key: "freight_gross", label: "Gross freight", wrap: true, money: true },
    ...freightAdjColumns.map((col) => ({ key: `frt-${col.key}`, label: col.label, wrap: true, money: true })),
    { key: "freight_net", label: "Net freight", wrap: true, money: true },
    { key: "freight_party", label: "Paid by party", wrap: true, money: true },
    { key: "freight_bcpl", label: "By BCPL", wrap: true, money: true },
  ];

  const moneyCell = (value, enabled = true) => (
    <td className={`px-2 py-2 whitespace-nowrap text-right tabular text-[13px] ${enabled ? moneyToneClass(value) : "text-muted-foreground"}`}>
      {enabled ? formatMoney2(value) : "—"}
    </td>
  );

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-border/70">
        <table className="w-full text-sm min-w-[1200px]" data-testid="financial-adjustments-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.06em] font-medium text-muted-foreground bg-muted border-b border-border">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={[
                    "px-2 py-2 align-bottom text-center",
                    column.wrap ? "whitespace-normal leading-tight max-w-[5.5rem]" : "whitespace-nowrap",
                  ].join(" ")}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const expenseByKey = Object.fromEntries(
                (row.expenses || []).map((exp) => [exp.key || exp.code || exp.label, exp.amount]),
              );
              const freightAdjByKey = Object.fromEntries(
                (row.freight_adjustments || []).map((adj) => [adj.key || adj.code || adj.label, adj.amount]),
              );
              return (
                <tr key={row.id || row.voucher_id} className="border-b border-border/60 align-top">
                  <td className="px-2 py-2 whitespace-nowrap">{formatListDate(row.date)}</td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {row.voucher_id ? (
                      <button
                        type="button"
                        className="font-mono link-brand"
                        onClick={() => setVoucherPreview({
                          id: row.voucher_id,
                          code: row.voucher_code,
                          label: "Material",
                          rowHint: row,
                        })}
                      >
                        {row.voucher_code}
                      </button>
                    ) : (row.voucher_code || "—")}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">{row.invoice_no || "—"}</td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {row.purchase_id ? (
                      <button
                        type="button"
                        className="font-mono link-brand"
                        onClick={() => setPurchasePreview({
                          id: row.purchase_id,
                          code: row.purchase_code,
                        })}
                      >
                        {row.purchase_code}
                      </button>
                    ) : (row.purchase_code || "—")}
                  </td>
                  {moneyCell(row.gross_amount)}
                  {expenseColumns.map((col) => (
                    <React.Fragment key={col.key}>
                      {moneyCell(expenseByKey[col.key], expenseByKey[col.key] != null)}
                    </React.Fragment>
                  ))}
                  {moneyCell(row.freight_advance)}
                  {moneyCell(row.net_amount)}
                  <td className="px-2 py-2 whitespace-nowrap">
                    {row.freight_voucher_id ? (
                      <button
                        type="button"
                        className="font-mono link-brand"
                        onClick={() => setVoucherPreview({
                          id: row.freight_voucher_id,
                          code: row.freight_voucher_code,
                          label: "Freight",
                          rowHint: row,
                        })}
                      >
                        {row.freight_voucher_code}
                      </button>
                    ) : "—"}
                  </td>
                  {moneyCell(row.freight_gross, Boolean(row.freight_voucher_id))}
                  {freightAdjColumns.map((col) => (
                    <React.Fragment key={col.key}>
                      {moneyCell(
                        freightAdjByKey[col.key],
                        Boolean(row.freight_voucher_id) && freightAdjByKey[col.key] != null,
                      )}
                    </React.Fragment>
                  ))}
                  {moneyCell(row.freight_net, Boolean(row.freight_voucher_id))}
                  {moneyCell(row.freight_paid_by_party, Boolean(row.freight_voucher_id))}
                  {moneyCell(row.freight_paid_by_bcpl, Boolean(row.freight_voucher_id))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <VoucherBriefDialog
        open={Boolean(voucherPreview)}
        onOpenChange={(open) => !open && setVoucherPreview(null)}
        voucherId={voucherPreview?.id}
        voucherCode={voucherPreview?.code}
        label={voucherPreview?.label}
        rowHint={voucherPreview?.rowHint}
      />
      <PurchaseBriefDialog
        open={Boolean(purchasePreview)}
        onOpenChange={(open) => !open && setPurchasePreview(null)}
        purchaseId={purchasePreview?.id}
        purchaseCode={purchasePreview?.code}
      />
    </>
  );
}

function LabAnalysisTable({ rows, weightedAvg, loading }) {
  if (!rows.length) {
    return <EmptyText text={loading ? "Loading lab analysis..." : "No lab analysis rows match these filters."} />;
  }

  const formatLab = (value, key) => {
    if (value == null || value === "") return "—";
    const num = Number(value);
    if (Number.isNaN(num)) return "—";
    if (key === "length_mm") return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return formatPct(num);
  };

  const formatRate = (value) => {
    if (value == null || value === "") return "—";
    if (typeof value === "number") return formatCurrency(value);
    return String(value);
  };

  const columns = [
    { key: "date", label: "Date" },
    { key: "voucher", label: "Voucher" },
    { key: "invoice", label: "Invoice no" },
    { key: "contract", label: "Contract number" },
    { key: "rate", label: "Contract rate" },
    { key: "net", label: "Net amount" },
    ...LAB_ANALYSIS_PARAMS.map((p) => ({ key: p.key, label: p.label, lab: true })),
    { key: "houdi", label: "Houdi" },
    { key: "stack", label: "Warehouse stack no" },
  ];

  const renderDataRow = (row, { avg = false } = {}) => {
    const params = row.lab_params || {};
    return (
      <tr
        key={avg ? "weighted-avg" : (row.id || `${row.purchase_id}-${row.contract_number}-${row.voucher_code}`)}
        className={[
          "border-b border-border/60 align-top",
          avg ? "bg-muted/70 font-medium" : "",
        ].join(" ")}
      >
        <td className="px-2 py-2 whitespace-nowrap">{avg ? "Weighted avg" : formatListDate(row.date)}</td>
        <td className="px-2 py-2 whitespace-nowrap font-mono">{avg ? "—" : (row.voucher_code || "—")}</td>
        <td className="px-2 py-2 whitespace-nowrap">{avg ? "—" : (row.invoice_no || "—")}</td>
        <td className="px-2 py-2 whitespace-nowrap">{avg ? "—" : (row.contract_number || "—")}</td>
        <td className="px-2 py-2 whitespace-nowrap text-right tabular">{formatRate(row.contract_rate)}</td>
        <td className={`px-2 py-2 whitespace-nowrap text-right tabular ${moneyToneClass(row.net_amount)}`}>
          {formatMoney2(row.net_amount)}
        </td>
        {LAB_ANALYSIS_PARAMS.map((param) => {
          const value = params[param.key] ?? row[`lab_${param.key}`];
          return (
            <td key={param.key} className="px-2 py-2 whitespace-nowrap text-right tabular text-[13px]">
              {formatLab(value, param.key)}
            </td>
          );
        })}
        <td className="px-2 py-2 whitespace-nowrap">{avg ? "—" : (row.houdi || "—")}</td>
        <td className="px-2 py-2 whitespace-nowrap">{avg ? "—" : (row.warehouse_stack_no || "—")}</td>
      </tr>
    );
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full text-sm min-w-[1400px]" data-testid="lab-analysis-table">
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.06em] font-medium text-muted-foreground bg-muted border-b border-border">
            {columns.map((column) => (
              <th
                key={column.key}
                className={[
                  "px-2 py-2 align-bottom text-center",
                  column.lab ? "whitespace-normal leading-tight max-w-[4.5rem]" : "whitespace-nowrap",
                ].join(" ")}
              >
                {column.label}
              </th>
            ))}
          </tr>
          {weightedAvg ? renderDataRow(weightedAvg, { avg: true }) : null}
        </thead>
        <tbody>
          {rows.map((row) => renderDataRow(row))}
        </tbody>
      </table>
    </div>
  );
}

function SimpleTable({ columns, rows, emptyMessage }) {
  if (!rows.length) return <EmptyText text={emptyMessage} />;
  return (
    <div className="overflow-x-auto rounded-lg border border-border/70">
      <table className="w-full text-sm min-w-[960px]">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground bg-muted border-b border-border">
            {columns.map((column) => (
              <th key={column} className="px-3 py-2">{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-border/60 align-top">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-3 py-2 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
